import type { ReactElement } from "react";
import { X } from "lucide-react";
import { Combobox } from "../../../../shared/components/ui/combobox";
import { GENRE_OPTIONS } from "../../../../utils/genreMapping";

interface GenreMultiSelectProps {
  readonly value: string[];
  readonly onChange: (genres: string[]) => void;
  readonly disabled?: boolean;
  readonly containerClassName?: string;
  readonly badgeClassName?: string;
  readonly comboboxTriggerClassName?: string;
  readonly searchInputClassName?: string;
}

type Genre = (typeof GENRE_OPTIONS)[number];

/**
 * Multi-select component for genres
 * Shows selected genres as badges with remove buttons
 * Can be used in both inline table editing and full form contexts
 */
export function GenreMultiSelect({
  value,
  onChange,
  disabled,
  containerClassName = "flex items-center justify-between gap-2 w-full",
  comboboxTriggerClassName = "w-6 h-6 p-0 border-none",
  searchInputClassName = "h-9",
}: Readonly<GenreMultiSelectProps>): ReactElement {
  const handleAdd = (genreValue: string | null) => {
    if (genreValue && !value.includes(genreValue)) {
      onChange([...value, genreValue]);
    }
  };

  const handleRemove = (genreValue: string) => {
    onChange(value.filter((g) => g !== genreValue));
  };

  const availableGenres = GENRE_OPTIONS.filter((genre) => !value.includes(genre.value));
  const selectedGenres = value
    .map((genreName) => GENRE_OPTIONS.find((genre) => genre.value === genreName))
    .filter((genre): genre is Genre => genre !== undefined);

  return (
    <div className={`${containerClassName} relative`}>
      {/* Selected genre badges */}
      <div className="flex items-center gap-1 flex-wrap flex-1">
        {selectedGenres.length === 0 && <span className="text-xs text-neutral-500 italic">add genre</span>}
        {selectedGenres.map(({ value: genreName, label }) => (
          <div key={genreName} className="text-xs text-lumi-accent items-center flex gap-1 px-1 py-1.5">
            <span>{label}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(genreName)}
                className="text-neutral-400 hover:text-white"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add genre combobox - wrapper positioned relative for absolute dropdown */}
      {!disabled && availableGenres.length > 0 && (
        <Combobox
          value={null}
          onValueChange={handleAdd}
          options={availableGenres}
          placeholder="+"
          searchPlaceholder="Search"
          emptyMessage="No genre found."
          disabled={disabled}
          fullWidthDropdown={true}
          triggerClassName={comboboxTriggerClassName}
          buttonClassName="hover:bg-transparent"
          textClassName="text-base font-normal"
          iconClassName="hidden"
          dropdownClassName="text-xs mt-1 py-4 h-[200px] rounded-lg"
          itemClassName="text-xs py-1.5"
          searchInputClassName={searchInputClassName}
        />
      )}
    </div>
  );
}
